import React, {
	useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Socket, { Message } from '../../connection/socket';
import { GreenroomState } from '../../stores/types/types';
import * as NotificationActions from '../../stores/actions/notifications';
import LiveCounter from '../general-components/live-counter';
import Icon, { COLORS } from '../general-components/icon';
import { NOTIFICATION_STYLES } from '../../stores/reducers/notifications';
import { durationString } from '../../utils/utils';
import useShots from '../../contexts/shots/provider';
import { useVideoMachine } from '../../contexts/video-machine';

interface OwnProps {
	// props that will be passed to this component
	socket: Socket | null;
	userIsInCanvas: boolean;
	userIsInNextShot: boolean;
}

export default function NotificationsView(props: OwnProps) {
	const dispatch = useDispatch();
	const notification = useSelector(
		(state: GreenroomState) => state.NotificationsReducer.notification,
	);
	const shotStartTimestampEntity = useSelector(
		(state: GreenroomState) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const clearShot = useSelector((state: GreenroomState) => state.SettingsReducer.clearShot);
	const {
		socket,
		userIsInCanvas,
		userIsInNextShot,
	} = props;

	const {
		currentShot,
		currentShot: {
			shot_data: {
				shotDuration,
				autoDuration,
			},
		},
	} = useShots();

	const { videoMachineState } = useVideoMachine();

	const playingTimeout = useRef(undefined as undefined | NodeJS.Timeout);
	const [count, setCount] = useState<number>(0);

	function playCountdown() {
		if (currentShot && shotStartTimestampEntity) {
			const startTime = new Date(shotStartTimestampEntity.valueOf());
			const endTime = startTime.getTime() + (shotDuration * 1000);
			const countdownSync = ((endTime - Date.now()) % 1000);
			let countdown = Math.round((endTime - Date.now()) / 1000);

			if (
				autoDuration
				&& (videoMachineState.timeLeft || videoMachineState.elapsedPercent === 100)
			) {
				countdown = Math.round(videoMachineState.timeLeft);
			}

			setCount(countdown);
			playingTimeout.current = setTimeout(() => {
				playCountdown();
			}, countdown > 0 ? countdownSync : 1000 + countdownSync);
		}
	}

	function close() {
		dispatch(NotificationActions.removeNotification());
		notification?.onClose?.();
	}

	useEffect(() => {
		if (socket) {
			socket.addListener('request-unmute', (mess: Message) => {
				console.log(mess);
			});
			socket.addListener('request-show-video', (mess: Message) => {
				console.log(mess);
			});
			socket.addListener('live-in', (mess: Message) => {
				console.log(mess);
			});
			socket.addListener('screen-sharing', (mess: Message) => {
				console.log(mess);
			});
		}

		return () => {
			socket?.removeListener('request-unmute');
			socket?.removeListener('request-show-video');
			socket?.removeListener('live-in');
			socket?.removeListener('screen-sharing');
		};
	}, [socket]);

	useEffect(() => {
		if (currentShot && shotStartTimestampEntity && (userIsInNextShot || userIsInCanvas)) {
			playCountdown();
		} else if (playingTimeout.current) {
			clearTimeout(playingTimeout.current);
		} else {
			setCount(0);
		}

		return () => {
			if (playingTimeout.current) clearTimeout(playingTimeout.current);
		};
	}, [
		currentShot,
		shotDuration,
		autoDuration,
		shotStartTimestampEntity,
		userIsInNextShot,
		userIsInCanvas,
		videoMachineState.elapsedPercent,
		videoMachineState.timeLeft,
	]);

	const countdownInMinutes = useMemo(
		() => (count !== null && count > 60 ? Math.ceil(count / 60) : null),
		[count],
	);
	const countdownHasEnded = useMemo(() => count !== null && count < 1, [count]);

	return (
		<div className={classNames('notifications-wrapper')}>
			<div className="notifications-container">
				{!!notification && !userIsInCanvas
					? (
						<div className={classNames('notification', { action: !!notification.onClick, subtitle: !!notification.subtitle })}>
							{/* eslint-disable-next-line no-nested-ternary */}
							{notification.type === NOTIFICATION_STYLES.TIMED && countdownInMinutes
								? (
									!clearShot ? (
										<div className="notification-center">
											Your next shot in {countdownInMinutes} minutes
										</div>
									) : (
										<></>
									)
								) : (
									<>
										<div className="notification-left">
											{notification.showLive
												? <><LiveCounter isHeader={false} /></>
												: <span>{notification.type === NOTIFICATION_STYLES.TIMED && count > 0
													? `${notification.title} in ${count} sec`
													: notification.title}</span>
											}
										</div>
										<div className="notification-right">
											{notification.onClick
												? <div className="notification-action-container">
													<button className="notification-action" onClick={notification.onClick}>
														{!!notification.icon && (
															<Icon name={notification.icon} size={13} color={COLORS.WHITE} />
														)}
														{notification.subtitle}
													</button>
													<button className="notification-close" onClick={close}><FontAwesomeIcon icon={faTimes} /></button>
												</div>
												: <>
													{!!notification.subtitle && (
														<span className="notification-subtitle">{!!notification.icon && (
															<Icon name={notification.icon} size={13} color={COLORS.WHITE} />
														)} {notification.subtitle}</span>
													)}
												</>
											}
										</div>
									</>
								)}

						</div>
					) : (
						<>
							{shotStartTimestampEntity && userIsInCanvas ? (
								<div className="live-indicator presenter-main-video">
									<span className={classNames('timer-text-presenter-live', countdownHasEnded && 'flashing')}>
										<span>{!countdownHasEnded ? durationString(count, true) : '00 : 00'}</span>
									</span>
								</div>
							) : (<></>)}
						</>
					)}
			</div>
		</div>
	);
}
